/**
 * Context paroisse active (multi-tenant V1.5 — F1.P4).
 *
 * Source de vérité unique pour le `parishId` que les pages doivent utiliser
 * pour leurs fetch (au lieu de l'ancienne const `DEMO_PARISH_ID`).
 *
 * Logique :
 *  1. À la connexion, le BFF renvoie `user.parishes[]` + `user.primaryParishId`.
 *  2. Ce provider lit ces champs :
 *     - 1 paroisse  → `currentParishId = parishes[0].id` (auto)
 *     - N paroisses → `currentParishId = primaryParishId` (par défaut), choix
 *                     possible via `setCurrentParish(id)` (persisté en
 *                     localStorage, restauré au refresh)
 *     - 0 paroisse  → `currentParishId = null`, le caller doit rediriger
 *                     vers `/select-parish` (utile pour edaci_admin)
 *  3. Pour les admins globaux qui ont sélectionné une paroisse côté
 *     `/select-parish`, leur choix est aussi persisté en localStorage et
 *     restauré à chaque session.
 *
 * Les pages utilisent `useCurrentParishId()` (hook hoisted) au lieu de
 * `DEMO_PARISH_ID` (qui devient un fallback dev-only).
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth, type ParishMembership } from "@/hooks/use-auth";
import { queryClient } from "@/lib/queryClient";

interface ParishContextValue {
  /** Paroisse active (null = aucun choix, redirect vers /select-parish). */
  currentParishId: string | null;
  /** Nom de la paroisse active (résolution best-effort). */
  currentParishName: string | null;
  /** Paroisses du user (membership avec rôle + flag primaire). */
  availableParishes: ParishMembership[];
  /** Indique si le user est un admin global (parishes vides + roles admin). */
  isGlobalAdmin: boolean;
  /** Change la paroisse active. Persiste en localStorage. */
  setCurrentParish: (parishId: string) => void;
  /** Réinitialise le choix (pour edaci_admin qui revient à la page selecteur). */
  clearCurrentParish: () => void;
}

const ParishContext = createContext<ParishContextValue | null>(null);

const STORAGE_KEY = "paroisses_current_parish_id";

const ADMIN_GLOBAL_ROLES = new Set([
  "edaci_admin",
  "super_admin",
  "admin",
  "vicariat_admin",
]);

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(value: string | null) {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore (SSR, mode privé...) */
  }
}

export function ParishProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [stored, setStored] = useState<string | null>(() => readStored());

  // À chaque login/logout, on resync le choix.
  //
  // `isAuthLoading` est indispensable : au rechargement de page, `user` vaut
  // null tant que GET /api/user est en vol. Sans ce garde-fou on purgeait le
  // localStorage à chaque F5 et l'admin global était renvoyé sur
  // /select-parish (constaté en prod le 2026-08-04).
  useEffect(() => {
    if (isAuthLoading) return;
    if (!user) {
      // Logout (ou session expirée) → on clear le choix persistant
      setStored(null);
      writeStored(null);
    }
  }, [user, isAuthLoading]);

  // Paroisses de TRAVAIL seulement (19/10/2026) : le rattachement de fidèle
  // choisi dans CATHO+ (rôle « paroissien ») ne fixe pas la paroisse active.
  // Le BFF le retire déjà ; ce filtre couvre une session ouverte avant lui.
  const availableParishes = useMemo<ParishMembership[]>(
    () => (user?.parishes ?? []).filter((p) => String(p.role ?? "").toLowerCase() !== "paroissien"),
    [user?.parishes],
  );

  const isGlobalAdmin = useMemo(() => {
    const roles = user?.roles ?? [];
    return roles.some((r) => ADMIN_GLOBAL_ROLES.has(r));
  }, [user?.roles]);

  const currentParishId = useMemo<string | null>(() => {
    // Si l'user a choisi explicitement une paroisse (stockée), on l'utilise
    // SI elle est dans ses paroisses OU s'il est admin global (peut tout choisir).
    if (stored) {
      if (isGlobalAdmin) return stored;
      const match = availableParishes.some((p) => p.id === stored);
      if (match) return stored;
    }
    // Sinon, paroisse primaire si elle existe ET reste une paroisse de travail
    // (un administrateur rattaché comme fidèle n'y est plus enfermé).
    if (user?.primaryParishId && availableParishes.some((p) => p.id === user.primaryParishId)) {
      return user.primaryParishId;
    }
    // Sinon, première paroisse de la liste (cas où primaryParishId absent
    // mais parishes pas vide).
    if (availableParishes.length > 0) return availableParishes[0].id;
    // Aucun choix possible → caller doit rediriger.
    return null;
  }, [stored, isGlobalAdmin, availableParishes, user?.primaryParishId]);

  const currentParishName = useMemo<string | null>(() => {
    if (!currentParishId) return null;
    const m = availableParishes.find((p) => p.id === currentParishId);
    return m?.name ?? null;
  }, [currentParishId, availableParishes]);

  const setCurrentParish = (parishId: string) => {
    if (parishId === currentParishId) return;
    setStored(parishId);
    writeStored(parishId);
    // Re-fetch toutes les données paroisse-scoped. Sans ça les écrans
    // déjà montés (dashboard, intentions, etc.) gardent le cache du
    // parishId précédent et le switch passe inaperçu.
    queryClient.invalidateQueries();
  };

  const clearCurrentParish = () => {
    setStored(null);
    writeStored(null);
    queryClient.invalidateQueries();
  };

  return (
    <ParishContext.Provider
      value={{
        currentParishId,
        currentParishName,
        availableParishes,
        isGlobalAdmin,
        setCurrentParish,
        clearCurrentParish,
      }}
    >
      {children}
    </ParishContext.Provider>
  );
}

export function useParish(): ParishContextValue {
  const ctx = useContext(ParishContext);
  if (!ctx) throw new Error("useParish must be used within ParishProvider");
  return ctx;
}

/**
 * Hook de convenance : retourne l'UUID de la paroisse active.
 * Lance une erreur si null (caller doit avoir géré la redirection).
 * Pour un accès permissif, lire `useParish().currentParishId` directement.
 */
export function useCurrentParishId(): string {
  const { currentParishId } = useParish();
  if (!currentParishId) {
    throw new Error(
      "useCurrentParishId(): aucune paroisse active. " +
        "Le composant doit être derrière un guard <RequireParish>.",
    );
  }
  return currentParishId;
}

/**
 * Variante permissive : retourne `string | null` sans throw.
 * Utile pour les composants qui doivent fonctionner sans paroisse
 * (ex: sidebar admin avant sélection).
 */
export function useCurrentParishIdOrNull(): string | null {
  return useParish().currentParishId;
}
